import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import EmployeeAvatar from '../../components/EmployeeAvatar'
import { Button } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import { DEPARTMENT_TYPES } from '../../config/departmentTypes'
import './Company.css'

function StageAllocationEdit() {
  const { id: idParam } = useParams()
  const stageId = idParam ? decodeURIComponent(idParam) : ''
  const navigate = useNavigate()
  const { user } = useAuth()
  const canManageAllocation = user?.role === 'admin' || user?.role === 'management'

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [data, setData] = useState({
    assigned_employees: [],
    teams: [],
  })
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId])

  const fetchData = async () => {
    if (!stageId) return
    try {
      setLoading(true)
      setError('')
      const response = await api.get(`/company/stages/${encodeURIComponent(stageId)}/allocations`)
      const nextData = {
        assigned_employees: response.data?.assigned_employees || [],
        teams: response.data?.teams || [],
      }
      setData(nextData)
      setSelectedEmployeeIds(nextData.assigned_employees.map((employee) => employee.employee_id).filter(Boolean))
    } catch (requestError) {
      setError(translateApiError(requestError.response?.data?.error || requestError.message))
      setData({ assigned_employees: [], teams: [] })
      setSelectedEmployeeIds([])
    } finally {
      setLoading(false)
    }
  }

  const selectedByDepartment = useMemo(() => {
    const selectedSet = new Set(selectedEmployeeIds)
    const grouped = {}
    ;(data.teams || []).forEach((team) => {
      ;(team.members || []).forEach((member) => {
        if (!selectedSet.has(member.employee_id)) return
        const department = member.department || team.department_name || 'Без подразделения'
        if (!grouped[department]) grouped[department] = []
        if (!grouped[department].some((item) => item.employee_id === member.employee_id)) {
          grouped[department].push(member)
        }
      })
    })
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([departmentName, members]) => ({
        department_name: departmentName,
        members: members.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')),
      }))
  }, [data.teams, selectedEmployeeIds])

  const toggleEmployee = (employeeId) => {
    setSelectedEmployeeIds((prev) => (
      prev.includes(employeeId)
        ? prev.filter((idValue) => idValue !== employeeId)
        : [...prev, employeeId]
    ))
  }

  const toggleTeam = (team) => {
    const memberIds = (team?.members || []).map((member) => member.employee_id).filter(Boolean)
    if (memberIds.length === 0) return
    const allSelected = memberIds.every((memberId) => selectedEmployeeIds.includes(memberId))
    setSelectedEmployeeIds((prev) => {
      if (allSelected) {
        return prev.filter((memberId) => !memberIds.includes(memberId))
      }
      return Array.from(new Set([...prev, ...memberIds]))
    })
  }

  const isTeamSelected = (team) => {
    const memberIds = (team?.members || []).map((member) => member.employee_id).filter(Boolean)
    return memberIds.length > 0 && memberIds.every((memberId) => selectedEmployeeIds.includes(memberId))
  }

  const save = async () => {
    if (!stageId) return
    try {
      setSaving(true)
      setError('')
      await api.put(`/company/stages/${encodeURIComponent(stageId)}/allocations`, {
        employee_ids: selectedEmployeeIds,
      })
      navigate(`/stage/${encodeURIComponent(stageId)}`)
    } catch (requestError) {
      setError(translateApiError(requestError.response?.data?.error || requestError.message))
    } finally {
      setSaving(false)
    }
  }

  if (!canManageAllocation) {
    return (
      <div className="company-page">
        <div className="page-header">
          <Button type="button" variant="ghost" onClick={() => navigate(`/stage/${encodeURIComponent(stageId)}`)} className="btn-back">
            ← Назад к стадии
          </Button>
          <h1>Исполнители стадии</h1>
        </div>
        <p className="info-note">Редактирование доступно только роли «Руководство».</p>
      </div>
    )
  }

  return (
    <div className="company-page stage-allocation-edit-page">
      <div className="page-header">
        <Button type="button" variant="ghost" onClick={() => navigate(`/stage/${encodeURIComponent(stageId)}`)} className="btn-back">
          ← Назад к стадии
        </Button>
        <h1>Исполнители стадии</h1>
        <div className="page-header-actions">
          <Button type="button" variant="secondary" onClick={fetchData} disabled={loading || saving}>
            Обновить
          </Button>
          <Button type="button" variant="primary" onClick={save} disabled={loading || saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </div>

      {error && <div className="message message-error">{error}</div>}

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          <div className="section-card">
            <div className="section-card-header">
              <h2>Выбранные исполнители</h2>
            </div>
            {selectedByDepartment.length === 0 ? (
              <p className="info-note">Исполнители не выбраны.</p>
            ) : (
              <div className="stage-allocation-selected-grid">
                {selectedByDepartment.map((group) => (
                  <div key={group.department_name} className="stage-allocation-selected-card">
                    <h3>{group.department_name}</h3>
                    <div className="stage-allocation-selected-members">
                      {group.members.map((member) => (
                        <div key={member.employee_id} className="stage-allocation-selected-member">
                          <EmployeeAvatar
                            employee={{ full_name: member.full_name, avatar_url: member.avatar_url }}
                            size="small"
                            showStatus={false}
                            showTooltip={true}
                          />
                          <span>{member.full_name}</span>
                          <button
                            type="button"
                            className="stage-allocation-remove-btn"
                            title="Убрать"
                            onClick={() => toggleEmployee(member.employee_id)}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="section-card">
            <div className="section-card-header">
              <h2>Команды</h2>
            </div>
            {(data.teams || []).filter(t => t.department_type === DEPARTMENT_TYPES.PRODUCTION).length === 0 ? (
              <p className="info-note">Команды не найдены.</p>
            ) : (
              <div className="stage-allocation-team-cards">
                {(data.teams || []).filter(t => t.department_type === DEPARTMENT_TYPES.PRODUCTION).map((team) => (
                  <div key={team.team_id} className="stage-allocation-team-card ui-card">
                    <div className="stage-allocation-team-card-head">
                      <div>
                        <h3>{team.team_name}</h3>
                        <p className="info-note">{team.department_name} · {(team.members || []).length} сотрудников</p>
                      </div>
                      <Button type="button" variant={isTeamSelected(team) ? 'secondary' : 'primary'} onClick={() => toggleTeam(team)}>
                        {isTeamSelected(team) ? 'Убрать команду' : '+ Добавить команду'}
                      </Button>
                    </div>
                    <div className="stage-allocation-team-members">
                      {(team.members || []).map((member) => (
                        <label key={member.employee_id} className="stage-allocation-member-option">
                          <input
                            type="checkbox"
                            checked={selectedEmployeeIds.includes(member.employee_id)}
                            onChange={() => toggleEmployee(member.employee_id)}
                          />
                          <EmployeeAvatar
                            employee={{ full_name: member.full_name, avatar_url: member.avatar_url }}
                            size="small"
                            showStatus={false}
                            showTooltip={true}
                          />
                          <span>{member.full_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default StageAllocationEdit
